<section class="products-page">
  <div class="page-heading">
    <div>
      <p>Marketplace management</p>
      <h2>{{ createOnly ? 'Create product' : reviewOnly ? 'Listing approvals' : 'Products' }}</h2>
    </div>
    @if (!createOnly) { <button type="button" (click)="loadProducts()">
      <span class="material-symbols-outlined" aria-hidden="true">refresh</span>
      Refresh
    </button> }
  </div>

  @if (createOnly) {
  <section class="panel">
    <div class="panel-title">
      <h3>Create listing</h3>
      @if (isMetaLoading) {
        <span class="hint">Loading options...</span>
      }
    </div>
    @if (errorMessage) {
      <p class="state error form-message" role="alert">{{ errorMessage }}</p>
      @if (needsProfitRuleSetup && isPlatformMode) {
        @if (canManageProfitRules) { <button class="profit-setup-action" type="button" (click)="openProfitRuleSetup()">
          <span class="material-symbols-outlined" aria-hidden="true">percent</span>
          Set up profit rule for this category
        </button> }
      }
    }
    @if (successMessage) {
      <p class="state success form-message" role="status" [class.edit-notice]="successMessageTone === 'edit'" [class.danger-notice]="successMessageTone === 'danger'">{{ successMessage }}</p>
    }
    <section class="listing-location" [class.empty-location]="!selectedStore">
      <span class="location-icon material-symbols-outlined" aria-hidden="true">storefront</span>
      <div class="listing-location-copy">
        <span>Listing location</span>
        @if (selectedStore) {
          <strong>{{ selectedStore.name }}</strong>
          <p>{{ selectedStoreAddress }}</p>
          <small>{{ isPlatformMode ? 'Platform inventory location' : 'Business location' }}</small>
        } @else {
          <strong>No business location selected</strong>
          <p>{{ sellerType === 'Business' ? 'Select a business during seller verification to load its locations.' : 'Choose the platform location receiving this product.' }}</p>
        }
      </div>
      @if (stores.length) {
        <button class="location-action" type="button" (click)="changeLocation()">
          <span class="material-symbols-outlined" aria-hidden="true">swap_horiz</span>
          {{ selectedStore ? 'Change location' : 'Choose location' }}
        </button>
      }
    </section>
    <div class="form-steps" aria-label="Product creation progress">
      <span [class.active]="currentStep === 1"><b>1</b> Product details</span>
      <span [class.active]="currentStep === 2"><b>2</b> Seller verification</span>
    </div>
    <form class="product-form" (ngSubmit)="currentStep === 1 ? goToSellerVerification() : createProduct()">
      @if (currentStep === 1) {
      <input name="title" [(ngModel)]="form.title" placeholder="Product title" required />
      <select name="listingType" [(ngModel)]="form.listingType" (ngModelChange)="onProductTypeChange()">
        <option value="New">New product</option>
        <option value="Used">Used product</option>
      </select>
      <select name="parentCategoryId" [(ngModel)]="formParentCategoryId" (ngModelChange)="onFormCategoryChange()" required>
        <option value="" disabled>Select main category</option>
        @for (category of parentCategories; track category.id) {
          <option [value]="category.id">{{ category.name }}</option>
        }
        @if (!parentCategories.length) { <option value="" disabled>No categories available</option> }
      </select>
      <select name="subCategoryId" [(ngModel)]="formSubCategoryId" (ngModelChange)="onFormSubCategoryChange()" required>
        <option value="" disabled>Select category</option>
        @for (category of formChildCategories; track category.id) {
          <option [value]="category.id">{{ category.name }}</option>
        }
        @if (!formChildCategories.length) { <option value="" disabled>No categories available</option> }
      </select>
      @if (formSubChildCategories.length) {
        <select name="categoryId" [(ngModel)]="form.categoryId" (ngModelChange)="onFormSubChildCategoryChange()" required>
          <option value="" disabled>Select subcategory</option>
          @for (category of formSubChildCategories; track category.id) {
            <option [value]="category.id">{{ category.name }}</option>
          }
        </select>
      }
      <select name="brandId" [(ngModel)]="form.brandId">
        <option value="" disabled>Select brand (optional)</option>
        @for (brand of brands; track brand.id) {
          <option [value]="brand.id">{{ brand.name }}</option>
        }
        @if (!brands.length) { <option value="" disabled>No brands available</option> }
      </select>
      <input name="modelName" [(ngModel)]="form.modelName" placeholder="Model name" />
      <label class="price-input">
        <span>Cost</span>
        <input
          type="number"
          name="cost"
          [(ngModel)]="form.cost"
          aria-label="Cost"
          min="0.01"
          step="1"
          required
        />
      </label>
      @if (form.listingType === 'Used') {
        <label class="price-input">
          <span>Repair cost</span>
          <input type="number" name="repairCost" [(ngModel)]="form.repairCost" aria-label="Repair cost" min="0" step="1" required />
        </label>
      }
      <label class="price-input">
        <span>Extra cost</span>
        <input type="number" name="extraCost" [(ngModel)]="form.extraCost" aria-label="Extra cost" min="0" step="1" />
      </label>
      <label class="ready-toggle">
        <input type="checkbox" name="isReadyToSell" [(ngModel)]="form.isReadyToSell" />
        <span>Ready to sell</span>
      </label>
      <label class="file-picker">
        <span class="material-symbols-outlined" aria-hidden="true">image</span>
        Upload images
        <input #productImagesInput type="file" accept="image/*" multiple (change)="onImageFilesChange($event)" />
      </label>
      @if (form.existingImageUrls?.length) {
        <div class="aadhaar-panel verified"><div class="aadhaar-status"><span class="material-symbols-outlined">photo_library</span><span><strong>{{ form.existingImageUrls?.length }} purchase image(s) reused</strong><small>No image upload is required again. These images will be verified when you publish on the marketplace.</small></span></div></div>
        <div class="image-preview-grid">@for (url of form.existingImageUrls; track url) { <figure class="image-preview-card"><img [src]="url" alt="Purchased product image" /></figure> }</div>
      }
      @if (form.imageFiles?.length) {
        <div class="image-preview-grid" aria-label="Selected product image previews">
          @for (preview of imagePreviews; track preview.url; let imageIndex = $index) {
            <figure class="image-preview-card">
              <img [src]="preview.url" [alt]="preview.name" />
              <button
                class="remove-preview-image"
                type="button"
                title="Remove image"
                aria-label="Remove image"
                (click)="removeProductImage(imageIndex, productImagesInput)"
              >
                <span class="material-symbols-outlined" aria-hidden="true">close</span>
              </button>
            </figure>
          }
        </div>
      }
      <div class="rich-editor">
        <div class="editor-toolbar" role="toolbar" aria-label="Description formatting">
          <button type="button" title="Bold" aria-label="Bold" (mousedown)="$event.preventDefault()" (click)="formatDescription('bold')"><span class="material-symbols-outlined">format_bold</span></button>
          <button type="button" title="Italic" aria-label="Italic" (mousedown)="$event.preventDefault()" (click)="formatDescription('italic')"><span class="material-symbols-outlined">format_italic</span></button>
          <button type="button" title="Heading" aria-label="Heading" (mousedown)="$event.preventDefault()" (click)="formatDescription('formatBlock', 'h3')"><span class="material-symbols-outlined">format_h2</span></button>
          <button type="button" title="Bulleted list" aria-label="Bulleted list" (mousedown)="$event.preventDefault()" (click)="formatDescription('insertUnorderedList')"><span class="material-symbols-outlined">format_list_bulleted</span></button>
          <button type="button" title="Numbered list" aria-label="Numbered list" (mousedown)="$event.preventDefault()" (click)="formatDescription('insertOrderedList')"><span class="material-symbols-outlined">format_list_numbered</span></button>
          <span class="toolbar-divider"></span>
          <button type="button" title="Undo" aria-label="Undo" (mousedown)="$event.preventDefault()" (click)="formatDescription('undo')"><span class="material-symbols-outlined">undo</span></button>
          <button type="button" title="Redo" aria-label="Redo" (mousedown)="$event.preventDefault()" (click)="formatDescription('redo')"><span class="material-symbols-outlined">redo</span></button>
        </div>
        <div
          #descriptionEditor
          class="editor-content"
          contenteditable="true"
          dir="ltr"
          role="textbox"
          aria-label="Product description"
          aria-multiline="true"
          data-placeholder="Product description"
          (input)="onDescriptionInput($event)"
          (paste)="onDescriptionPaste($event)"
        ></div>
      </div>
      <div class="step-actions continue-actions">
        <button class="continue-button" type="submit" [disabled]="!canContinueToVerification">
          <span>
            <strong>Continue</strong>
            <small>{{ form.listingType === 'Used' && !purchaseItemId ? 'Seller verification' : 'Review product setup' }}</small>
          </span>
          <span class="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
        </button>
      </div>
      } @else {
        @if (isPlatformMode && form.listingType === 'Used' && !purchaseItemId) {
          <div class="seller-type-control">
            <label [class.active]="sellerType === 'Individual'">
              <input type="radio" name="sellerType" value="Individual" [(ngModel)]="sellerType" (ngModelChange)="onSellerTypeChange()" />
              <span class="material-symbols-outlined" aria-hidden="true">person</span>
              <span><strong>Individual</strong><small>Verify using phone and Aadhaar</small></span>
            </label>
            <label [class.active]="sellerType === 'Business'">
              <input type="radio" name="sellerType" value="Business" [(ngModel)]="sellerType" (ngModelChange)="onSellerTypeChange()" />
              <span class="material-symbols-outlined" aria-hidden="true">business</span>
              <span><strong>Business</strong><small>Select from registered businesses</small></span>
            </label>
          </div>
        }
        @if (isPlatformMode && sellerType === 'Business' && !purchaseItemId) {
          <select name="sellerTenantId" [(ngModel)]="form.sellerTenantId" (ngModelChange)="onSellerBusinessChange($event)" required>
            <option value="" disabled>Select seller business</option>
            @for (business of sellerBusinesses; track business.id) { <option [value]="business.id">{{ business.businessName }}</option> }
          </select>
        }
        @if (isPlatformMode && form.listingType === 'Used' && !purchaseItemId && sellerType === 'Individual') {
          <div class="individual-lookup">
            <div class="verification-heading">
              <span class="material-symbols-outlined" aria-hidden="true">verified_user</span>
              <span><strong>Verify individual seller</strong><small>Customer must have a registered ZepBazaar mobile number.</small></span>
            </div>
            <label>
              <span>Registered mobile number</span>
              <input name="individualSellerPhone" [ngModel]="individualSellerPhone" (ngModelChange)="onIndividualSellerPhoneInput($event)" inputmode="numeric" autocomplete="tel" maxlength="10" pattern="[0-9]{10}" placeholder="Enter 10-digit mobile number" />
            </label>
            <label>
              <span>Aadhaar number</span>
              <input type="password" name="aadhaarNumber" [(ngModel)]="aadhaarNumber" inputmode="numeric" autocomplete="off" maxlength="12" placeholder="Enter 12-digit Aadhaar number" />
            </label>
            <button type="button" [disabled]="isSellerLookupBusy || isAadhaarBusy" (click)="startIndividualVerification()">
              <span class="material-symbols-outlined" aria-hidden="true">send_to_mobile</span>
              {{ isSellerLookupBusy || isAadhaarBusy ? 'Checking...' : 'Fetch details & send OTP' }}
            </button>
          </div>
        }
        @if (isPlatformMode && form.listingType === 'Used' && !purchaseItemId && sellerType === 'Individual' && selectedCustomer) {
          <div class="aadhaar-panel" [class.verified]="isSelectedCustomerVerified">
            <div class="aadhaar-status">
              <span class="material-symbols-outlined" aria-hidden="true">{{ isSelectedCustomerVerified ? 'verified_user' : 'badge' }}</span>
              <span>
                <strong>
                  {{ isSelectedCustomerVerified
                    ? (selectedCustomer?.aadhaarVerifiedName || selectedCustomer?.firstName + ' ' + selectedCustomer?.lastName)
                    : 'Aadhaar ' + (selectedCustomer?.aadhaarVerificationStatus || 'not verified') }}
                </strong>
                <small>{{ selectedCustomer?.maskedAadhaarNumber || 'Verification required' }} | {{ selectedCustomer?.phoneNumber }}</small>
                <small>Live selfie: {{ selectedCustomer?.livenessVerificationStatus || 'not verified' }}{{ selectedCustomer?.livenessExpiresAt ? ' · expires ' + (selectedCustomer?.livenessExpiresAt | date:'mediumDate') : '' }}</small>
              </span>
            </div>
            @if (!isSelectedCustomerVerified) {
              @if (aadhaarReferenceId) {
                <input name="aadhaarOtp" [(ngModel)]="aadhaarOtp" inputmode="numeric" autocomplete="one-time-code" maxlength="8" placeholder="Enter OTP" />
                <button type="button" [disabled]="isAadhaarBusy" (click)="verifyAadhaarOtp()">Verify</button>
              }
            }
          </div>
        }
        @if (isPlatformMode && form.listingType === 'Used' && !purchaseItemId) {
          <div class="evidence-options">
            <div class="evidence-upload">
              <label class="verification-photo"><span class="material-symbols-outlined" aria-hidden="true">photo_camera</span><span><strong>Seller/customer with product</strong><small>Select image</small></span><input #sellerPhotoInput type="file" accept="image/*" capture="environment" (change)="onSellerVerificationImageChange($event)" /></label>
              @if (sellerImagePreviewUrl) {
                <figure class="evidence-preview">
                  <img [src]="sellerImagePreviewUrl" alt="Seller with product preview" />
                  <button class="remove-preview-image" type="button" title="Remove image" aria-label="Remove seller image" (click)="clearSellerVerificationImage(sellerPhotoInput)"><span class="material-symbols-outlined">close</span></button>
                </figure>
              }
            </div>
            <span class="evidence-or">OR</span>
            <div class="evidence-upload">
              <label class="verification-photo"><span class="material-symbols-outlined" aria-hidden="true">receipt_long</span><span><strong>Original bill</strong><small>Select image or PDF</small></span><input #originalBillInput type="file" accept="image/*,application/pdf" (change)="onOriginalBillChange($event)" /></label>
              @if (billPreviewUrl) {
                <figure class="evidence-preview" [class.pdf-preview]="billIsPdf">
                  @if (billIsPdf) {
                    <a [href]="billPreviewUrl" target="_blank" rel="noopener" title="Open PDF bill" aria-label="Open PDF bill"><span class="material-symbols-outlined">picture_as_pdf</span><strong>View PDF</strong></a>
                  } @else {
                    <img [src]="billPreviewUrl" alt="Original bill preview" />
                  }
                  <button class="remove-preview-image" type="button" title="Remove bill" aria-label="Remove bill" (click)="clearOriginalBill(originalBillInput)"><span class="material-symbols-outlined">close</span></button>
                </figure>
              }
            </div>
          </div>
        }
        @if (isPlatformMode && form.listingType === 'Used' && purchaseItemId) {
          <div class="aadhaar-panel verified">
            <div class="aadhaar-status">
              <span class="material-symbols-outlined" aria-hidden="true">verified_user</span>
              <span><strong>Seller verification already collected</strong><small>The purchase evidence is securely reused. No Aadhaar OTP, selfie, or bill upload is required again.</small></span>
            </div>
          </div>
        }
        <div class="step-actions">
          <button class="secondary" type="button" (click)="backToProductDetails()"><span class="material-symbols-outlined" aria-hidden="true">arrow_back</span>Back</button>
          <button type="submit" [disabled]="isSubmitting || !canCreateProduct"><span class="material-symbols-outlined" aria-hidden="true">add_box</span>{{ isSubmitting ? 'Listing...' : 'Create listing' }}</button>
        </div>
      }
    </form>
  </section>

  @if (isLocationPickerOpen) {
    <div class="dialog-backdrop" role="presentation" (click)="closeLocationPicker()">
      <section class="location-dialog" role="dialog" aria-modal="true" aria-labelledby="location-dialog-title" (click)="$event.stopPropagation()">
        <div class="dialog-heading">
          <div>
            <h3 id="location-dialog-title">Choose business location</h3>
            <p>The listing address will be taken from this location.</p>
          </div>
          <button class="dialog-close" type="button" title="Close" aria-label="Close location picker" (click)="closeLocationPicker()">
            <span class="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </div>
        <div class="location-list">
          @for (store of stores; track store.id) {
            <label class="location-choice" [class.selected]="pendingStoreId === store.id">
              <input type="radio" name="productLocation" [value]="store.id" [(ngModel)]="pendingStoreId" />
              <span class="material-symbols-outlined" aria-hidden="true">storefront</span>
              <span>
                <strong>{{ store.name }}</strong>
                <small>{{ store.addressLine2 || store.addressLine1 || store.area }}, {{ store.city }}, {{ store.state }} {{ store.postalCode }}</small>
              </span>
            </label>
          }
        </div>
        <div class="dialog-actions">
          <button class="secondary" type="button" (click)="closeLocationPicker()">Cancel</button>
          <button type="button" [disabled]="!pendingStoreId" (click)="confirmLocationChange()">
            <span class="material-symbols-outlined" aria-hidden="true">check</span>
            Use location
          </button>
        </div>
      </section>
    </div>
  }
  }

  @if (!createOnly) {
  @if (!reviewOnly) {
  <section class="panel">
    <div class="panel-title">
      <h3>Find products</h3>
      <div class="quick-actions">
        <button class="secondary" type="button" (click)="listSellerProducts()">Seller list</button>
        <button class="secondary" type="button" (click)="listCustomerOldProducts()">Customer old items</button>
      </div>
    </div>

    <form class="filters" (ngSubmit)="loadProducts()">
      <input name="search" [(ngModel)]="filters.search" placeholder="Search product" />
      <select name="filterListingType" [(ngModel)]="filters.listingType">
        <option value="">New and used</option>
        <option>New</option>
        <option>Used</option>
      </select>
      <select name="filterStore" [(ngModel)]="filterStoreId">
        <option value="">All locations</option>
        @for (store of stores; track store.id) { <option [value]="store.id">{{ store.name }} · {{ store.city }}</option> }
      </select>
      <select name="filterParentCategory" [(ngModel)]="filterParentCategoryId" (ngModelChange)="onFilterCategoryChange()">
        @for (category of parentCategories; track category.id) {
          <option [value]="category.id">{{ category.name }}</option>
        }
      </select>
      <select name="filterSubCategory" [(ngModel)]="filterSubCategoryId" (ngModelChange)="onFilterSubCategoryChange($event)">
        @for (category of filterChildCategories; track category.id) {
          <option [value]="category.id">{{ category.name }}</option>
        }
      </select>
      @if (filterSubChildCategories.length) {
        <select name="filterSubChildCategory" [(ngModel)]="filterCategoryId" (ngModelChange)="onFilterSubChildCategoryChange($event)">
        @for (category of filterSubChildCategories; track category.id) {
            <option [value]="category.id">{{ category.name }}</option>
        }
        </select>
      }
      <button type="submit">
        <span class="material-symbols-outlined" aria-hidden="true">search</span>
        Search
      </button>
    </form>
  </section>
  } @else {
    <section class="approval-summary">
      <span class="material-symbols-outlined" aria-hidden="true">fact_check</span>
      <div>
        <h3>Products waiting for review</h3>
        <p>Check the product details, seller and images, then approve it to publish or reject it.</p>
      </div>
      <strong>{{ products.length }} pending</strong>
    </section>
  }

  @if (isLoading) {
    <section class="product-list-skeleton" aria-live="polite" aria-busy="true">
      <strong>Loading products</strong>
      @for (row of [1, 2, 3, 4]; track row) {
        <div class="product-skeleton-row"><span></span><span></span><span></span><span></span><span></span></div>
      }
    </section>
  }

  @if (successMessage) {
    <p class="state success" [class.edit-notice]="successMessageTone === 'edit'" [class.danger-notice]="successMessageTone === 'danger'">{{ successMessage }}</p>
  }

  @if (errorMessage) {
    <p class="state error">{{ errorMessage }}</p>
  }

  <section class="table-panel">
    <table>
      <thead>
        <tr>
          <th>Title</th>
          <th>Type</th>
          <th>Qty</th>
          <th>Listed location</th>
          <th>Cost</th>
          <th>Repair cost</th>
          <th>Selling price</th>
          <th>Status</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        @for (product of visibleProducts; track product.id) {
          <tr class="product-row" tabindex="0" (click)="openProductView(product)" (keydown.enter)="openProductView(product)">
            <td>
              <strong>{{ product.title }}</strong>
              <small>{{ descriptionText(product.description) }}</small>
            </td>
            <td><span class="status status-{{ displayCondition(product) === 'New' ? 'blue' : 'orange' }}">{{ displayCondition(product) }}</span></td>
            <td><strong>{{ displayQuantity(product) }}</strong></td>
            <td><strong>{{ productLocationName(product) }}</strong><small>{{ displayLocation(product) || productStreetAddress(product) }}</small></td>
            <td>{{ product.cost == null ? 'Not set' : formatPrice(product.cost) }}</td>
            <td>{{ product.repairCost == null ? 'Not set' : formatPrice(product.repairCost) }}</td>
            <td>{{ formatPrice(product.price) }}</td>
            <td>
              <span class="status status-{{ statusClass(product.status) }}">{{ displayStatus(product.status) }}</span>
              @if ((displayStatus(product.status) === 'Rejected' || displayStatus(product.status) === 'Reported' || displayStatus(product.status) === 'On hold' || displayStatus(product.status) === 'OnHold') && productModerationReason(product)) {
                <button type="button" class="reason-link" (click)="$event.stopPropagation(); openProductView(product)">Why?</button>
              }
            </td>
            <td>
              <div class="moderation-actions">
                <button type="button" class="secondary" (click)="$event.stopPropagation(); openProductView(product)"><span class="material-symbols-outlined">visibility</span>View</button>
                @if (canEditProduct) { <button type="button" class="edit-action" [disabled]="isOpeningEdit || editingProduct || isSavingEdit || moderatingProductId === product.id" (click)="$event.stopPropagation(); openEditProduct(product)"><span class="material-symbols-outlined">edit</span>{{ isOpeningEdit ? 'Opening…' : 'Edit' }}</button> }
                @if (displayStatus(product.status) === 'Rejected') {
                  <button type="button" class="appeal-action" [disabled]="moderatingProductId === product.id" (click)="$event.stopPropagation(); appealProduct(product)"><span class="material-symbols-outlined">rate_review</span>Appeal</button>
                }
              </div>
            </td>
          </tr>
        } @empty {
          <tr>
            <td colspan="9" class="empty">No products found.</td>
          </tr>
        }
      </tbody>
    </table>
  </section>
  @if (hasMoreProducts) {
    <p class="product-scroll-state"><span class="loader-ring"></span>{{ isLoadingMore ? 'Loading 10 more products…' : 'Scroll to load 10 more products' }}</p>
  } @else if (products.length) {
    <p class="product-scroll-state complete"><span class="material-symbols-outlined">check_circle</span>No more products</p>
  }

  @if (editingProduct) {
    <div class="dialog-backdrop" role="presentation" (click)="closeEditProduct()">
      <section class="location-dialog product-edit-dialog" role="dialog" aria-modal="true" aria-labelledby="edit-product-title" (click)="$event.stopPropagation()">
        <div class="dialog-heading"><div><h3 id="edit-product-title">Edit product</h3><p>Update the essential listing and price information.</p></div><button class="dialog-close" type="button" title="Close" (click)="closeEditProduct()"><span class="material-symbols-outlined">close</span></button></div>
        <form class="edit-product-form" (ngSubmit)="saveEditedProduct()">
          <label><span>Title</span><input name="editTitle" [(ngModel)]="editForm.title" required minlength="5" /></label>
          <label><span>Model name</span><input name="editModel" [(ngModel)]="editForm.modelName" /></label>
          <label class="wide"><span>Description</span><textarea name="editDescription" [(ngModel)]="editForm.description" rows="4"></textarea></label>
          <label><span>Cost</span><input type="number" name="editCost" [(ngModel)]="editForm.cost" min="0" /></label>
          <label><span>Repair cost</span><input type="number" name="editRepairCost" [(ngModel)]="editForm.repairCost" min="0" /></label>
          <label><span>Extra cost</span><input type="number" name="editExtraCost" [(ngModel)]="editForm.extraCost" min="0" /></label>
          <label><span>Selling price</span><input type="number" name="editSellingPrice" [(ngModel)]="editForm.sellingPrice" min="0.01" required /></label>
          <label class="ready-edit-toggle"><span>Ready to sell</span><input type="checkbox" name="editReadyToSell" [(ngModel)]="editForm.isReadyToSell" /></label>
          <div class="wide edit-images">
            <div><strong>Product images</strong><small>Existing images are preserved. Select more files to add them to the gallery.</small></div>
            @if (editingProduct.images?.length) {
              <div class="edit-image-grid existing-images">
                @for (image of editingProduct.images || []; track $index) { @if (imageUrl(image)) { <img [src]="imageUrl(image)" [alt]="editingProduct.title" /> } }
              </div>
            }
            <label class="edit-image-picker"><span class="material-symbols-outlined">add_photo_alternate</span><span><strong>Add more images</strong><small>JPG, PNG, WEBP or HEIC</small></span><input #editImagesInput type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple (change)="onEditImagesChange($event)" /></label>
            @if (editImagePreviewUrls.length) {
              <div class="edit-image-grid">
                @for (preview of editImagePreviewUrls; track preview; let index = $index) {
                  <figure><img [src]="preview" [alt]="'Replacement image ' + (index + 1)" /><button type="button" title="Remove image" (click)="removeEditImage(index, editImagesInput)"><span class="material-symbols-outlined">close</span></button></figure>
                }
              </div>
            }
          </div>
          <div class="dialog-actions wide"><button class="secondary" type="button" [disabled]="isSavingEdit" (click)="closeEditProduct()">Cancel</button><button type="submit" [disabled]="isSavingEdit">{{ isSavingEdit ? 'Saving…' : 'Save changes' }}</button></div>
        </form>
      </section>
    </div>
  }

  @if (appealingProduct) {
    <div class="dialog-backdrop" role="presentation" (click)="closeAppeal()">
      <section class="location-dialog appeal-dialog" role="dialog" aria-modal="true" aria-labelledby="appeal-title" (click)="$event.stopPropagation()">
        <div class="dialog-heading"><div><p>Request another review</p><h3 id="appeal-title">Appeal {{ appealingProduct.title }}</h3></div><button class="dialog-close" type="button" title="Close" [disabled]="isSubmittingAppeal" (click)="closeAppeal()"><span class="material-symbols-outlined">close</span></button></div>
        <div class="appeal-current-reason"><span class="material-symbols-outlined">info</span><div><strong>Current moderation reason</strong><p>{{ productModerationReason(appealingProduct) || 'No moderation reason was provided.' }}</p></div></div>
        <label class="appeal-reason"><span>What did you fix?</span><textarea [(ngModel)]="appealReason" rows="5" maxlength="1000" placeholder="Describe the corrections made to the product, images, price, description, or ownership details."></textarea><small>{{ appealReason.trim().length }}/1000 · Minimum 10 characters</small></label>
        <div class="dialog-actions"><button class="secondary" type="button" [disabled]="isSubmittingAppeal" (click)="closeAppeal()">Cancel</button><button type="button" [disabled]="isSubmittingAppeal || appealReason.trim().length < 10" (click)="submitAppeal()"><span class="material-symbols-outlined">rate_review</span>{{ isSubmittingAppeal ? 'Submitting…' : 'Submit appeal' }}</button></div>
      </section>
    </div>
  }

  @if (viewingProduct) {
    <div class="dialog-backdrop" role="presentation" (click)="closeProductView()">
      <section class="location-dialog product-view-dialog" role="dialog" aria-modal="true" aria-labelledby="view-product-title" (click)="$event.stopPropagation()">
        <div class="dialog-heading"><div><p>Product overview</p><h3 id="view-product-title">{{ viewingProduct.title }}</h3></div><button class="dialog-close" type="button" title="Close" (click)="closeProductView()"><span class="material-symbols-outlined">close</span></button></div>
        <div class="product-view-gallery">
          @for (image of viewingProduct.images || []; track $index) {
            @if (imageUrl(image)) { <img [src]="imageUrl(image)" [alt]="viewingProduct.title" /> }
          } @empty { <div class="view-image-empty"><span class="material-symbols-outlined">image_not_supported</span>No product images</div> }
        </div>
        <div class="product-view-price"><strong>₹{{ formatPrice(viewingProduct.price) }}</strong><span class="status status-{{ statusClass(viewingProduct.status) }}">{{ displayStatus(viewingProduct.status) }}</span></div>
        <p class="product-view-description">{{ descriptionText(viewingProduct.description) }}</p>
        <div class="product-view-grid">
          <div><span>Seller</span><strong>{{ viewingProduct.sellerName || 'Name unavailable' }}</strong></div>
          <div><span>Condition</span><strong>{{ displayCondition(viewingProduct) || 'Unspecified' }}</strong></div>
          <div><span>Category</span><strong>{{ productCategoryName(viewingProduct) }}</strong></div>
          <div><span>Brand</span><strong>{{ productBrandName(viewingProduct) }}</strong></div>
          <div><span>Model</span><strong>{{ viewingProduct.modelName || 'Not specified' }}</strong></div>
          <div><span>Location name</span><strong>{{ productLocationName(viewingProduct) }}</strong></div>
          <div class="wide"><span>Full location</span><strong>{{ productStreetAddress(viewingProduct) }}, {{ displayLocation(viewingProduct) }}</strong></div>
          <div><span>Cost</span><strong>{{ viewingProduct.cost == null ? 'Not set' : '₹' + formatPrice(viewingProduct.cost) }}</strong></div>
          <div><span>Repair cost</span><strong>{{ viewingProduct.repairCost == null ? 'Not set' : '₹' + formatPrice(viewingProduct.repairCost) }}</strong></div>
          <div><span>Extra cost</span><strong>{{ viewingProduct.extraCost == null ? 'Not set' : '₹' + formatPrice(viewingProduct.extraCost) }}</strong></div>
          <div><span>Selling price</span><strong>₹{{ formatPrice(viewingProduct.price) }}</strong></div>
          <div><span>Ready to sell</span><strong>{{ viewingProduct.isReadyToSell === false ? 'No' : 'Yes' }}</strong></div>
          <div><span>Views</span><strong>{{ viewingProduct.viewCount || 0 }}</strong></div>
          <div><span>Interested</span><strong>{{ viewingProduct.interestCount || 0 }}</strong></div>
          @if (viewingProduct.automatedDecision || productModerationReason(viewingProduct)) {
            <div class="wide moderation-detail"><span>Why this status?</span><strong>{{ viewingProduct.automatedDecision || displayStatus(viewingProduct.status) }}</strong><p>{{ productModerationReason(viewingProduct) || 'No moderation reason was provided.' }}</p></div>
          }
        </div>
        <div class="dialog-actions">
          <button class="secondary" type="button" (click)="closeProductView()">Close</button>
          @if (canEditProduct) { <button type="button" (click)="editViewedProduct()"><span class="material-symbols-outlined">edit</span>Edit product</button> }
          @if (displayStatus(viewingProduct.status) === 'Rejected') { <button class="appeal-action" type="button" (click)="appealProduct(viewingProduct); closeProductView()"><span class="material-symbols-outlined">rate_review</span>Appeal after fixing</button> }
        </div>
      </section>
    </div>
  }
  }
</section>
